import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 초기 로그인 상태 확인
    checkUser();

    // 세션 변경 감지
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // 검색한 사용자 정보가 있으면 세션 변경 무시
        const searchedUser = localStorage.getItem("searchedUser");
        const searchedUserProfile = localStorage.getItem("searchedUserProfile");
        if (searchedUser && searchedUserProfile) {
          return;
        }

        if (session) {
          // 세션 만료 시간 확인
          const expiresAt = new Date(session.expires_at * 1000).getTime();
          const now = new Date().getTime();

          if (now > expiresAt) {
            // 세션이 만료된 경우
            await handleLogout();
          } else {
            // 세션에서 유저 정보 가져오기
            setUser(session.user);
          }
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // user가 바뀔 때마다 프로필 fetch
  useEffect(() => {
    const fetchUserProfile = async () => {
      // 검색한 사용자 정보가 있으면 프로필 fetch 무시
      const searchedUser = localStorage.getItem("searchedUser");
      const searchedUserProfile = localStorage.getItem("searchedUserProfile");
      if (searchedUser && searchedUserProfile) {
        return;
      }

      if (!user?.email) {
        setUserProfile(null);
        return;
      }
      const { data, error } = await supabase
        .from("user")
        .select("name, nickname, img_profile, role, admin, partner")
        .eq("email", user.email)
        .single();
      if (!error) setUserProfile(data);
      else setUserProfile(null);
    };
    fetchUserProfile();
  }, [user]);

  const checkUser = async () => {
    try {
      // 먼저 검색한 사용자 정보가 있는지 확인
      const searchedUser = localStorage.getItem("searchedUser");
      const searchedUserProfile = localStorage.getItem("searchedUserProfile");
      if (searchedUser && searchedUserProfile) {
        setUser(JSON.parse(searchedUser));
        setUserProfile(JSON.parse(searchedUserProfile));
        setLoading(false);
        return;
      }

      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session) {
        // 세션 만료 시간 확인
        const expiresAt = new Date(session.expires_at * 1000).getTime();
        const now = new Date().getTime();

        if (now > expiresAt) {
          // 세션이 만료된 경우
          await handleLogout();
        } else {
          // 사용자 정보 한 번에 조회
          const { data: userData, error: userError } = await supabase
            .from("user")
            .select("*")
            .eq("email", session.user.email)
            .single();

          if (!userError) {
            setUser({
              ...session.user,
              ...userData,
            });
            setUserProfile(userData);
          } else {
            setUser(session.user);
          }
        }
      }
    } catch (error) {
      console.error("Error checking user session:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // 로컬 스토리지의 토큰 제거
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");

      // 검색한 사용자 정보도 제거
      localStorage.removeItem("searchedUser");
      localStorage.removeItem("searchedUserProfile");

      // Supabase 로그아웃
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // 사용자 상태 초기화
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  const searchUserByEmail = async (email) => {
    try {
      // 현재 어드민 정보 저장
      const currentUser = user;
      const currentUserProfile = userProfile;
      localStorage.setItem("adminUser", JSON.stringify(currentUser));
      localStorage.setItem(
        "adminUserProfile",
        JSON.stringify(currentUserProfile)
      );

      // 사용자 정보 한 번에 조회
      const { data: userData, error: userError } = await supabase
        .from("user")
        .select("*")
        .eq("email", email)
        .single();

      if (userError) throw userError;

      // 어드민이 검색한 사용자 정보를 user에 설정
      setUser({
        ...userData,
        email: userData.email,
        id: userData.id,
      });
      setUserProfile(userData);

      // 검색한 사용자 정보를 localStorage에 저장
      localStorage.setItem("searchedUser", JSON.stringify(userData));
      localStorage.setItem("searchedUserProfile", JSON.stringify(userData));

      return { user: userData, profile: userData };
    } catch (error) {
      console.error("Error searching user:", error);
      throw error;
    }
  };

  const restoreAdminUser = () => {
    const adminUser = localStorage.getItem("adminUser");
    const adminUserProfile = localStorage.getItem("adminUserProfile");
    if (adminUser && adminUserProfile) {
      setUser(JSON.parse(adminUser));
      setUserProfile(JSON.parse(adminUserProfile));
      localStorage.removeItem("searchedUser");
      localStorage.removeItem("searchedUserProfile");
      localStorage.removeItem("adminUser");
      localStorage.removeItem("adminUserProfile");
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    logout: handleLogout,
    searchUserByEmail,
    restoreAdminUser,
    setUser,
    setUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
